import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { motion } from 'framer-motion';
import { Star, Loader2, CheckCircle2, BadgeCheck, Package, Calendar, Gift, ShoppingCart, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSeo } from '@/lib/seo';
import { resolveFullName } from '@/lib/displayName';
import { EXPERIENCE_DATA } from '@/components/booking/bookingDefaults';
import ReviewDrawCard from '@/components/reviews/ReviewDrawCard';
import { format } from 'date-fns';

const CATEGORY_OPTIONS = [
  { key: 'products', label: 'Products', icon: Package },
  { key: 'experiences', label: 'Experiences', icon: Calendar },
  { key: 'gift_cards', label: 'Gift Cards', icon: Gift },
];

function experienceName(key) {
  return EXPERIENCE_DATA[key]?.name || (key ? key.replace(/_/g, ' ') : 'Experience');
}

function PurchaseTile({ item, active, onSelect, primary, secondary }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center justify-between gap-3 border p-4 text-left transition-all ${
        active
          ? 'border-primary bg-primary/10'
          : 'border-white/10 bg-[#111115] hover:border-white/25'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold truncate ${active ? 'text-primary' : 'text-white'}`}>{primary}</p>
        {secondary && <p className="text-white/35 text-xs mt-0.5 truncate">{secondary}</p>}
      </div>
      <div className={`w-5 h-5 shrink-0 border flex items-center justify-center ${active ? 'border-primary bg-primary' : 'border-white/20'}`}>
        {active && <Check className="w-3.5 h-3.5 text-white" />}
      </div>
    </button>
  );
}

export default function LeaveReview() {
  useSeo({
    title: 'Leave a Review — Motion Reality Sim Racing',
    description: 'Share your Motion Reality experience. Verified customer reviews help other racers discover us.',
  });

  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ author_name: '', author_location: '', quote: '', source_url: '', customer_email: '', review_category: 'products', order_id: '', product_id: '' });
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [purchases, setPurchases] = useState({ products: [], experiences: [], gift_cards: [] });
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    base44.auth.isAuthenticated().then(async (authed) => {
      if (authed) {
        const me = await base44.auth.me();
        setUser(me);
        setForm((prev) => ({
          ...prev,
          author_name: resolveFullName(me),
          customer_email: me.email || '',
        }));
        setLoadingPurchases(true);
        try {
          const res = await base44.functions.invoke('getReviewablePurchases', {});
          if (res && !res.error) setPurchases(res);
        } catch {
          /* ignore — selector just shows empty state */
        } finally {
          setLoadingPurchases(false);
        }
      }
    });
  }, []);

  const handleChange = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleCategoryChange = (cat) => {
    setForm((prev) => ({ ...prev, review_category: cat, order_id: '', product_id: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.author_name || !form.quote) {
      setError('Please add your name and your review.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await base44.functions.invoke('submitReview', {
        data: { ...form, rating },
      });
      if (res?.error) throw new Error(res.error);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentList = purchases[form.review_category] || [];

  const tileMeta = (item) => {
    if (form.review_category === 'products') {
      return {
        primary: item.name,
        secondary: `${item.date ? format(new Date(item.date), 'dd MMM yyyy') : ''}${item.amount ? ` · £${Number(item.amount).toFixed(2)}` : ''}`,
      };
    }
    if (form.review_category === 'experiences') {
      return {
        primary: experienceName(item.experience_type),
        secondary: `${item.date ? format(new Date(item.date), 'dd MMM yyyy') : ''}${item.status ? ` · ${item.status}` : ''}`,
      };
    }
    return {
      primary: item.code || 'Gift Card',
      secondary: `${item.date ? format(new Date(item.date), 'dd MMM yyyy') : ''}${item.amount ? ` · £${Number(item.amount).toFixed(2)}` : ''}`,
    };
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-primary flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </motion.div>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">THANK YOU</p>
          <h1 className="text-white font-black text-3xl uppercase mb-3">Review Received</h1>
          <p className="text-white/40 text-sm mb-8">
            Thanks for sharing your experience. Your review is now pending approval and will appear on our site once verified.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setForm((prev) => ({
                ...prev,
                author_name: resolveFullName(user),
                author_location: '',
                quote: '',
                source_url: '',
                review_category: 'products',
                order_id: '',
                product_id: '',
              }));
              setRating(5);
            }}
            className="border border-white/15 text-white/60 hover:text-white hover:border-white/30 font-bold text-xs tracking-widest px-6 py-3 transition-all"
          >
            WRITE ANOTHER
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">YOUR FEEDBACK</p>
          <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
            LEAVE A REVIEW
          </h1>
          <p className="text-white/40 text-sm max-w-lg">
            Tell other racers about your Motion Reality experience. Reviews from customers with a verified purchase are badged accordingly.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-12 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-3 block">THIS REVIEW RELATES TO *</Label>
            <div className="grid grid-cols-3 gap-3">
              {CATEGORY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = form.review_category === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => handleCategoryChange(opt.key)}
                    className={`flex flex-col items-center gap-2 border py-4 transition-all ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/10 bg-[#111115] text-white/50 hover:border-white/25 hover:text-white/80'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px] font-bold tracking-wider">{opt.label.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Previous purchase selector */}
          {user && (
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-3 block flex items-center gap-2">
                <ShoppingCart className="w-3.5 h-3.5" /> SELECT YOUR PURCHASE
              </Label>
              {loadingPurchases ? (
                <div className="flex items-center gap-2 text-white/30 text-xs border border-white/10 bg-[#111115] p-4">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" /> Loading your previous purchases…
                </div>
              ) : currentList.length === 0 ? (
                <div className="border border-white/10 bg-[#111115] p-4 text-white/30 text-xs">
                  No {form.review_category.replace('_', ' ')} found for your account. You can still submit a review below.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-64 overflow-y-auto pr-1">
                  {currentList.map((item) => {
                    const meta = tileMeta(item);
                    const active = form.order_id === item.id;
                    return (
                      <PurchaseTile
                        key={item.id}
                        item={item}
                        active={active}
                        onSelect={() => {
                          if (active) {
                            setForm((prev) => ({ ...prev, order_id: '', product_id: '' }));
                          } else {
                            setForm((prev) => ({
                              ...prev,
                              order_id: item.id,
                              product_id: form.review_category === 'products' ? (item.product_id || '') : '',
                            }));
                          }
                        }}
                        primary={meta.primary}
                        secondary={meta.secondary}
                      />
                    );
                  })}
                </div>
              )}
              {form.order_id && (
                <p className="text-primary/70 text-xs mt-2 flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5" /> Linked to your purchase — this review will be badged as verified.
                </p>
              )}
            </div>
          )}

          {/* Rating */}
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-3 block">YOUR RATING</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="p-1"
                  aria-label={`${n} star${n !== 1 ? 's' : ''}`}
                >
                  <Star
                    className={`w-7 h-7 transition-colors ${
                      n <= (hover || rating) ? 'text-primary fill-primary' : 'text-white/15'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">YOUR NAME *</Label>
              <Input
                value={form.author_name}
                onChange={(e) => handleChange('author_name', e.target.value)}
                placeholder="James T."
                className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
                required
              />
            </div>
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">LOCATION</Label>
              <Input
                value={form.author_location}
                onChange={(e) => handleChange('author_location', e.target.value)}
                placeholder="Liverpool"
                className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">YOUR REVIEW *</Label>
            <Textarea
              value={form.quote}
              onChange={(e) => handleChange('quote', e.target.value)}
              placeholder="Share your honest experience..."
              rows={5}
              className="bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60 resize-none"
              required
            />
          </div>

          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">EMAIL (FOR VERIFICATION)</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => handleChange('customer_email', e.target.value)}
              placeholder="your@email.com"
              className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
            />
            <p className="text-white/30 text-xs mt-2 flex items-center gap-1.5">
              <BadgeCheck className="w-3.5 h-3.5 text-primary" />
              Use the email from your order to earn the Verified Customer Review badge.
            </p>
          </div>

          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">LINK TO ORIGINAL REVIEW (OPTIONAL)</Label>
            <Input
              value={form.source_url}
              onChange={(e) => handleChange('source_url', e.target.value)}
              placeholder="https://uk.trustpilot.com/reviews/... or Google review URL"
              className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
            />
            <p className="text-white/30 text-xs mt-2">If this review was also left on Google or Trustpilot, paste the link so we can credit the source and be entered into a free monthly draw for great prizes.</p>
          </div>

          {error && (
            <div className="border border-red-500/20 bg-red-500/5 text-red-400 text-xs px-4 py-3">
              {error}
            </div>
          )}

          <ReviewDrawCard />

          <button
            type="submit"
            disabled={submitting}
            className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-8 py-3.5 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
            SUBMIT REVIEW
          </button>
        </form>
      </section>
    </div>
  );
}